<script setup lang="ts">
import api from "@/modules/api";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { pageStoreFactory } from "@/stores/factory/list-store-factory";
import { Mate } from "@/types/entity";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useHouseScene } from "../store/houseScene";
import { useMate } from "@/stores/mate";
import { onMounted } from "vue";
import { getMockName } from "@/views/Mate/Create/MateCreatePresetMock";

defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

const presetMateEntityStore = entityStoreFactory<Mate>("id");
const mateStore = useMate();
const { dataList, fetchNextPage, initListIfAbsent } = pageStoreFactory({
  entityStore: presetMateEntityStore,
  fetch: async (page: number) => {
    const res = await api.readPresetMatePage({
      pagination: {
        page: page,
        perPage: 10,
      },
      filter: {
        isPreset: 1,
      },
    });
    presetMateEntityStore.updateMap(res.page.data);
    return res;
  },
});
const houseSceneStore = useHouseScene();
const onConfirmInvite = async (mate: Mate) => {
  try {
    await showConfirmDialog({
      title: "邀请角色",
      message: "邀请后角色将进入小屋，并添加到你的角色列表中",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    });
    const loading = showLoadingToast("正在与角色建立连接...");
    try {
      await houseSceneStore.adaptCharacter(mate.id);
      emit("update:show", false);
    } finally {
      loading.close();
    }
  } catch (error) {
    // ignore cancel
    return;
  }
};

onMounted(async () => {});
</script>
<template>
  <VanPopup
    :show="show"
    @update:show="emit('update:show', $event)"
    position="bottom"
    overlay-class="hidden"
    class="h-[50%] w-full bg-base-300 overflow-hidden grid grid-rows-[auto_1fr]"
    teleport="body"
  >
    <div class="flex justify-between p-2 items-center">
      <div>选择邀请的角色</div>
      <button
        class="btn btn-sm btn-ghost btn-square"
        @click="emit('update:show', false)"
      >
        <i class="iconify mingcute--close-line" />
      </button>
    </div>
    <ThreadList
      :onPullLoad="() => initListIfAbsent(true)"
      :onScrollLoad="fetchNextPage"
      class="p-2"
    >
      <div class="flex flex-col gap-1 h-full">
        <div
          v-for="mate in dataList"
          :key="mate.id"
          class="cursor-pointer p-2 border hover:bg-base-200"
          @click="onConfirmInvite(mate)"
        >
          <div class="flex gap-2">
            <UserAvatar class="w-12 flex-shrink-0" :mate="mate" />
            <div class="flex flex-col gap-2 w-full">
              <div class="flex justify-between w-full">
                <div class="text-sm font-bold">{{ mate.name }}</div>

                <div class="text-shadow text-base-content/50 text-xs">
                  by {{ getMockName(mate.id!) }}
                </div>
              </div>
              <div
                class="prose prose-sm text-xs line-clamp-2"
                v-html="mate.bio || mate.personalityPrompt"
              ></div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-center gap-2 p-2 border">
          <p class="text-xs text-base-content/70">
            你可以添加你喜欢的人成为伙伴，并邀请TA进入小屋
          </p>

          <button
            class="btn btn-xs btn-outline btn-primary"
            @click="mateStore.clickCreateMate()"
          >
            前往创建 >
          </button>
        </div>
      </div>
    </ThreadList>
  </VanPopup>
</template>
